import { useCallback, useMemo, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { fees, getProperty, isPropertyId, properties } from '../data/units'
import { BookingRequestPayload, BookingResponse, BookingType, GuestDetails, PropertyId } from '../types/booking'
import { estimatePrice, nightsBetween } from '../utils/pricing'
import { addHoursToTime } from '../utils/time'
import { BookingApiError, submitBookingRequest } from '../utils/bookingApi'

export type FieldErrors = Partial<Record<'dates' | 'shoot' | 'guests' | 'fullName' | 'email' | 'phone', string>>
type Status = 'idle' | 'submitting' | 'success'

const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/
const phonePattern = /^\+?[\d\s()-]{7,20}$/

export function useBookingForm() {
  const [searchParams] = useSearchParams()
  const initialProperty = searchParams.get('property')

  const [bookingType, setBookingType] = useState<BookingType>(searchParams.get('type') === 'photoshoot' ? 'photoshoot' : 'apartment')
  const [propertyId, setPropertyId] = useState<PropertyId>(isPropertyId(initialProperty) ? initialProperty : properties[0].id)
  const [checkIn, setCheckIn] = useState<string | null>(null)
  const [checkOut, setCheckOut] = useState<string | null>(null)
  const [guests, setGuests] = useState(2)
  const [earlyCheckIn, setEarlyCheckIn] = useState(false)
  const [lateCheckout, setLateCheckout] = useState(false)
  const [shootDate, setShootDate] = useState<string | null>(null)
  const [startTime, setStartTime] = useState<string | null>(null)
  const [guest, setGuest] = useState<GuestDetails>({ fullName: '', email: '', phone: '', specialRequests: '' })
  const [errors, setErrors] = useState<FieldErrors>({})
  const [status, setStatus] = useState<Status>('idle')
  const [submitError, setSubmitError] = useState<string | null>(null)
  const [response, setResponse] = useState<BookingResponse | null>(null)

  const property = getProperty(propertyId)
  const maxGuests = bookingType === 'apartment' ? property.maxGuests : fees.photoshootMaxGuests
  const endTime = startTime ? addHoursToTime(startTime, fees.photoshootHours) : null

  const estimate = useMemo(
    () => estimatePrice({ bookingType, propertyId, checkIn, checkOut, earlyCheckIn, lateCheckout }),
    [bookingType, propertyId, checkIn, checkOut, earlyCheckIn, lateCheckout],
  )

  const clearError = (key: keyof FieldErrors) => setErrors((current) => ({ ...current, [key]: undefined }))

  const changeType = useCallback((type: BookingType) => {
    setBookingType(type)
    setErrors({})
    setSubmitError(null)
    const cap = type === 'apartment' ? getProperty(propertyId).maxGuests : fees.photoshootMaxGuests
    setGuests((current) => Math.min(current, cap))
  }, [propertyId])

  const changeProperty = useCallback((id: PropertyId) => {
    setPropertyId(id)
    setGuests((current) => Math.min(current, getProperty(id).maxGuests))
  }, [])

  const changeDates = useCallback((start: string | null, end: string | null) => {
    setCheckIn(start)
    setCheckOut(end)
    clearError('dates')
  }, [])

  const changeShootDate = useCallback((date: string | null) => {
    setShootDate(date)
    clearError('shoot')
  }, [])

  const changeStartTime = useCallback((time: string) => {
    setStartTime(time)
    clearError('shoot')
  }, [])

  const updateGuest = useCallback((field: keyof GuestDetails, value: string) => {
    setGuest((current) => ({ ...current, [field]: value }))
    if (field !== 'specialRequests') clearError(field)
  }, [])

  const validate = (): FieldErrors => {
    const next: FieldErrors = {}
    if (bookingType === 'apartment') {
      if (!checkIn || !checkOut || nightsBetween(checkIn, checkOut) < 1) next.dates = 'Choose a check-in and a check-out date.'
    } else if (!shootDate || !startTime) {
      next.shoot = 'Choose a shoot date and a start time.'
    }
    if (guests < 1 || guests > maxGuests) next.guests = `Guests must be between 1 and ${maxGuests}.`
    if (guest.fullName.trim().length < 2) next.fullName = 'Enter your full name.'
    if (!emailPattern.test(guest.email.trim())) next.email = 'Enter a valid email address.'
    if (!phonePattern.test(guest.phone.trim())) next.phone = 'Enter a valid phone number.'
    return next
  }

  const buildPayload = (): BookingRequestPayload => {
    const isStay = bookingType === 'apartment'
    return {
      bookingType: isStay ? 'Apartment' : 'Photoshoot',
      fullName: guest.fullName.trim(),
      email: guest.email.trim(),
      phone: guest.phone.trim(),
      propertyId: isStay ? propertyId : '',
      checkIn: isStay ? (checkIn ?? '') : (shootDate ?? ''),
      checkOut: isStay ? (checkOut ?? '') : '',
      guests,
      earlyCheckIn: isStay && earlyCheckIn,
      lateCheckout: isStay && lateCheckout,
      startTime: isStay ? '' : (startTime ?? ''),
      endTime: isStay ? '' : (endTime ?? ''),
      specialRequests: guest.specialRequests.trim(),
    }
  }

  const submit = async () => {
    if (status === 'submitting') return
    setSubmitError(null)
    const nextErrors = validate()
    setErrors(nextErrors)
    if (Object.keys(nextErrors).length > 0) {
      window.setTimeout(() => {
        document.querySelector('form [role="alert"]')?.scrollIntoView({ behavior: 'smooth', block: 'center' })
      }, 60)
      return
    }

    setStatus('submitting')
    try {
      const result = await submitBookingRequest(buildPayload())
      setResponse(result)
      setStatus('success')
      window.scrollTo({ top: 0, behavior: 'smooth' })
    } catch (error) {
      setStatus('idle')
      setSubmitError(
        error instanceof BookingApiError ? error.message : 'Something went wrong while sending your booking. Please try again.',
      )
    }
  }

  const reset = () => {
    setCheckIn(null)
    setCheckOut(null)
    setShootDate(null)
    setStartTime(null)
    setEarlyCheckIn(false)
    setLateCheckout(false)
    setGuest({ fullName: '', email: '', phone: '', specialRequests: '' })
    setErrors({})
    setSubmitError(null)
    setResponse(null)
    setStatus('idle')
  }

  return {
    bookingType,
    changeType,
    property,
    propertyId,
    changeProperty,
    checkIn,
    checkOut,
    changeDates,
    guests,
    setGuests,
    maxGuests,
    earlyCheckIn,
    lateCheckout,
    toggleEarly: () => setEarlyCheckIn((v) => !v),
    toggleLate: () => setLateCheckout((v) => !v),
    shootDate,
    changeShootDate,
    startTime,
    endTime,
    changeStartTime,
    guest,
    updateGuest,
    errors,
    status,
    submitError,
    response,
    estimate,
    submit,
    reset,
  }
}
